/**
 * useCmsProducts — published products, fresher than the bundle.
 *
 * The build-time snapshot (`products.generated.ts`) seeds state so
 * prerendered HTML is complete; `/api/cms/public/products?locale=` replaces
 * it on mount so a product edit in the CMS shows without a rebuild.
 *
 * The response is already collapsed to the requested locale (badge, title,
 * description, features, …), so the renderer can read `product.title` directly
 * without the per-locale `locales` map the generated file carries.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsProducts, type CmsProductIndexEntry } from '@/content/cms/products.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'

interface ProductsResp {
  products?: CmsProductIndexEntry[]
}

export function useCmsProducts(): CmsProductIndexEntry[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  const initial = cmsData?.products ?? cmsProducts
  const [products, setProducts] = useState<CmsProductIndexEntry[]>(initial)

  useEffect(() => {
    if (cmsData?.products) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/products?locale=${encodeURIComponent(locale)}${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<ProductsResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.products?.length) return
        setProducts(j.products)
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.products])

  return products
}
